import { Component, registerComponent } from 'js/component.js';

const componentName = 'static-country-list';
// Query params set by the RA header (racom-webcomponent) when it sends a user whose
// country has several languages here: the IP country code and the page they came from
const countryParam = 'country';
const sourceParam = 'source';
// Sent by the country-locale-mapping component on this page after it sets the `country` param
const showLocationEvent = 'static-country-list:show-location';
// Locale code at the start of a path, e.g. /fr-be.html or /fr-be/products.html
const localePathPattern = /^\/([a-z]{2}-[a-z]{2,3})((\.|\/|$).*)/;

/**
 * @returns The `source` param as a URL if it is a rockwellautomation.com page with a
 * locale segment (so it cannot be used to redirect elsewhere), otherwise null
 */
const getSourceUrl = () => {
    try {
        const sourceUrl = new URL(new URL(window.location.href).searchParams.get(sourceParam));
        const isRaPage = sourceUrl.protocol === 'https:'
            && sourceUrl.hostname.endsWith('.rockwellautomation.com')
            && !sourceUrl.username && !sourceUrl.password;
        return isRaPage && localePathPattern.test(sourceUrl.pathname) ? sourceUrl : null;
    } catch {
        return null;
    }
};

/** @returns The locale code of a country link (e.g. `fr-be` for /fr-be.html), or empty string */
const getLinkLocale = (link) => {
    try {
        return new URL(link.href).pathname.match(localePathPattern)?.[1] || '';
    } catch {
        return '';
    }
};

export class StaticCountryList extends Component {

    addEventListeners() {
        const previousPage = document.referrer;
        const sourceUrl = getSourceUrl();
        const anchors = document.body.getElementsByTagName('a');
        for (let i = 0; i < anchors.length; i += 1) {
            // modify No Preference link to clear langSite cookie
            if (/No Preference/.test(anchors[i].textContent)) {
                if (anchors[i].getAttribute('listeners') !== '1') {
                    anchors[i].addEventListener('click', (ev) => {
                        ev.preventDefault();
                        this.langSiteCookieUpdate('');
                        if (sourceUrl) {
                            window.location.href = sourceUrl.href;
                        }
                    });
                    anchors[i].setAttribute('listeners', '1');
                }
            }
            // modify Country Links to open homepage or specific page
            if (anchors[i].classList.contains('country__link')) {
                const selectedHref = anchors[i].getAttribute('href');
                const selectedSite = selectedHref.replace(/.*(\.com)?(\.cn|\/[a-z]{2}-[a-z]{2,3}).*/, '$2');
                if (!anchors[i].getAttribute('listeners')) {
                    anchors[i].addEventListener('click', () => {
                        this.langSiteCookieUpdate(selectedSite === '.cn' ? '/zh-cn.html' : `${selectedSite}.html`); // set langSite cookie
                    });
                    anchors[i].setAttribute('listeners', '1');
                }
                const selectedLocale = getLinkLocale(anchors[i]);
                if (sourceUrl && selectedLocale) {
                    // Sent here by the header: return to the exact source page (query included) in the selected locale
                    if (anchors[i].getAttribute('listeners') !== '3') {
                        anchors[i].addEventListener('click', (ev) => {
                            ev.preventDefault();
                            this.goToSourcePage(sourceUrl, selectedLocale);
                        });
                        anchors[i].setAttribute('listeners', '3');
                    }
                } else if (/rockwellautomation\.com/.test(previousPage)) {
                    if (anchors[i].getAttribute('listeners') !== '2') {
                        const preferredPage = previousPage.replace(/^(.+\.rockwellautomation\.com)(\.cn\/?|\/[a-z]{2}-[a-z]{2,3})(\/products\/details\.)?([^.]*)(\.html)?$/, `$1${selectedSite}$3$4${/\//.test(selectedSite) ? '.html' : ''}`);
                        if (preferredPage) {
                            anchors[i].addEventListener('click', (ev) => {
                                ev.preventDefault();
                                const preferredPageUrl = new URL(preferredPage);
                                if (preferredPageUrl.origin === window.location.origin) {
                                    fetch(preferredPage, { method: 'HEAD' })
                                        .then(() => { // if it exists, go to previous page in selected site
                                            window.location.href = preferredPage;
                                        })
                                        .catch(() => { // otherwise go to homepage of selected site
                                            window.location.href = `https://${window.location.hostname}${selectedSite}${/\//.test(selectedSite) ? '.html' : ''}`;
                                        });
                                } else {
                                    window.location.href = preferredPage;
                                }
                            });
                            anchors[i].setAttribute('listeners', '2');
                        }
                    }
                }
            }
        }
    }

    /**
     * Goes to the source page with its locale segment replaced by `locale`, keeping the rest of
     * the path, query and hash. Falls back to the locale homepage if that page does not exist.
     */
    goToSourcePage(sourceUrl, locale) {
        const [, , rest] = sourceUrl.pathname.match(localePathPattern);
        const targetUrl = `${sourceUrl.origin}/${locale}${rest}${sourceUrl.search}${sourceUrl.hash}`;
        const homeUrl = `${sourceUrl.origin}/${locale}.html`;
        if (sourceUrl.origin !== window.location.origin) {
            window.location.href = targetUrl; // A cross-origin HEAD request would be blocked by CORS
            return;
        }
        fetch(targetUrl, { method: 'HEAD' })
            .then((response) => {
                window.location.href = response.ok ? targetUrl : homeUrl;
            })
            .catch(() => {
                window.location.href = homeUrl;
            });
    }

    /**
     * When the header sent the user here with their IP country, shows that country's languages
     * as buttons at the top, in the order they are authored in the country list. The card goes
     * under the hero banner text, or above the country lists if there is no hero banner.
     */
    renderLocationBar() {
        const country = new URL(window.location.href).searchParams.get(countryParam)?.toLowerCase();
        const template = document.querySelector(`.${componentName}__location-template`);
        if (!country || !/^[a-z]{2}$/.test(country) || !template || document.querySelector(`.${componentName}__location`)) {
            return;
        }

        // Find the country entry whose links point to that country's locales, e.g. /fr-be.html and /en-be.html for `be`
        let countryLinks = [];
        let countryEl = null;
        document.querySelectorAll('.country__link').forEach((link) => {
            const entry = link.closest('.country');
            if (entry === countryEl || !getLinkLocale(link).endsWith(`-${country}`)) {
                return;
            }
            const entryLinks = [...entry.querySelectorAll('.country__link')].filter(entryLink => getLinkLocale(entryLink).endsWith(`-${country}`));
            if (entryLinks.length > countryLinks.length) {
                countryEl = entry;
                countryLinks = entryLinks;
            }
        });
        if (!countryEl) {
            return;
        }

        // Labels are authored in the same order as the links (e.g. Belgique | Belgium for Français | English)
        const allLinks = [...countryEl.querySelectorAll('.country__link')];
        const labels = [...countryEl.querySelectorAll('.country__label')].map(label => label.textContent.trim());
        const getLabel = link => (labels.length === allLinks.length ? labels[allLinks.indexOf(link)] : labels[0]) || '';

        const bar = template.content.firstElementChild.cloneNode(true);
        const buttons = bar.querySelector(`.${componentName}__location-buttons`);
        countryLinks.forEach((link) => {
            const button = document.createElement('a');
            button.className = `${componentName}__location-button`;
            button.href = link.href;
            button.textContent = `${getLabel(link)} (${link.textContent.trim()})`;
            // Behave exactly like the country link (save the cookie, return to the source page)
            button.addEventListener('click', (ev) => {
                ev.preventDefault();
                link.click();
            });
            buttons.append(button);
        });

        // Name the country in the page language if it has a link in it, e.g. "Not in Belgium?" on an English page.
        // The selection page has no lang attribute and is in English.
        const pageLanguage = (document.documentElement.lang || 'en').split('-')[0].toLowerCase();
        const pageLanguageLink = countryLinks.find(link => getLinkLocale(link).startsWith(`${pageLanguage}-`));
        const countryName = getLabel(pageLanguageLink || countryLinks[0]);
        const notIn = bar.querySelector(`.${componentName}__location-not-in`);
        notIn.textContent = notIn.dataset.text.replace('{0}', countryName);

        // The country lists start at the component holding the template (the first region, North America).
        // Scroll its heading to just below the sticky region sub-nav, like the sub-nav's own region links.
        const countryLists = template.closest(`.${componentName}`);
        bar.querySelector(`.${componentName}__location-choose`).addEventListener('click', (ev) => {
            ev.preventDefault();
            const subNavHeight = document.querySelector('.sub-nav')?.offsetHeight || 0;
            const listsTop = countryLists.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: listsTop - subNavHeight - 15, behavior: 'smooth' });
        });

        const heroText = document.querySelector('.hero-banner__text-container');
        if (heroText) {
            heroText.append(bar);
        } else {
            countryLists.before(bar);
        }
    }

    langSiteCookieUpdate(value) {
        const expiryDate = new Date();
        expiryDate.setTime(expiryDate.getTime() + (365 * 1000 * 60 * 60 * 24));
        const expiryValue = value === '' ? 'Thu, 01 Jan 1970 00:00:00 UTC' : expiryDate.toUTCString();
        document.cookie = `langSite=${escape(value)};expires=${expiryValue};path=/;secure;domain=.rockwellautomation.com;SameSite=strict`;
    }

    init() {
        this.addEventListeners();
        this.renderLocationBar();
        document.addEventListener(showLocationEvent, () => this.renderLocationBar());
    }
}

registerComponent(`.${componentName}`, StaticCountryList);
